import { ThreeEvent } from '@react-three/fiber';
import { useContextMenu } from 'components/common/context-menu-manager';
import { ContextMenuItemType, LevelType } from 'data/types';
import { use3DRightClick } from 'hooks/use-3d-right-click';
import { useDirectPointDrag } from 'hooks/use-direct-point-drag';
import { useDuctRunActions } from 'hooks/use-duct-run-actions';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MeshStandardMaterial } from 'three';
import {
  applyDuctDragOverride,
  computeSegmentTransform,
  computeZoneDuct3DGeometry,
  DuctDragOverride,
  DuctPoint3DGeometry,
  DuctRun3DGeometry,
  ductWorldToPx,
  isDuctPointDragUnchanged,
  resolveDuctPointDrag
} from 'utils/3d-duct-geometry';
import { ZoneConcealedDuctLayout } from 'utils/concealed-duct-geometry';
import { getConcealedDuctRunWorldHeight } from 'utils/concealed-duct-placement';
import {
  clampReturnEndpointPx,
  clampSupplyEndpointPx,
  PointPx
} from 'utils/duct-runs';
import { getGridSpacesForLevel } from 'utils/grid';
import { beginLinesetDrag, endLinesetDrag } from 'utils/lineset-drag-state';
import { TransformControlsInteractionState } from 'utils/transform-controls';
import DuctPoint3D from './duct-point-3d';
import DuctPointGizmo from './duct-point-gizmo';

const SUPPLY_COLOR = '#0ea5e9';
const RETURN_COLOR = '#f59e0b';
const DUCT_SHELL_COLOR = '#c3c9d1';
const DUCT_SHELL_METALNESS = 0.7;
const DUCT_SHELL_ROUGHNESS = 0.3;
const DUCT_SHELL_OPACITY = 0.92;
const DUCT_SHELL_REVEALED_OPACITY = 0.28;
const SUPPLY_DUCT_RADIUS = 0.11;
const RETURN_DUCT_RADIUS = 0.13;
const GUIDE_LINE_RADIUS = 0.02;
const DUCT_HIT_MARGIN = 0.05;
const ENDPOINT_RADIUS = 0.1;
const WAYPOINT_RADIUS = 0.075;
const REGISTER_PLATE_COLOR = '#eef0f2';
const SUPPLY_REGISTER_BOOT: [number, number, number] = [0.3, 0.22, 0.4];
const SUPPLY_REGISTER_PLATE: [number, number, number] = [0.34, 0.04, 0.52];
const RETURN_REGISTER_BOOT: [number, number, number] = [0.36, 0.26, 0.5];
const RETURN_REGISTER_PLATE: [number, number, number] = [0.4, 0.04, 0.64];

type SegmentEnds = {
  start: [number, number, number];
  end: [number, number, number];
};

function DuctGuideSegment({
  start,
  end,
  color,
  opacity
}: SegmentEnds & {
  color: string;
  opacity: number;
}) {
  const material = useMemo(
    () =>
      new MeshStandardMaterial({
        color,
        transparent: true,
        opacity
      }),
    [color, opacity]
  );

  const transform = computeSegmentTransform(start, end);
  if (!transform) return null;

  return (
    <mesh
      position={transform.position}
      quaternion={transform.quaternion}
      material={material}
    >
      <cylinderGeometry
        args={[GUIDE_LINE_RADIUS, GUIDE_LINE_RADIUS, transform.length, 8]}
      />
    </mesh>
  );
}

function DuctSegmentHitArea({
  start,
  end,
  hitRadius,
  onContextMenu,
  onHoverChange
}: SegmentEnds & {
  hitRadius: number;
  onContextMenu: (event: ThreeEvent<PointerEvent>) => void;
  onHoverChange: (hovered: boolean) => void;
}) {
  const rightClick = use3DRightClick(onContextMenu);
  const hoveredRef = useRef(false);
  const onHoverChangeRef = useRef(onHoverChange);

  onHoverChangeRef.current = onHoverChange;

  useEffect(() => {
    return () => {
      if (hoveredRef.current) {
        hoveredRef.current = false;
        onHoverChangeRef.current(false);
      }
    };
  }, []);

  const transform = computeSegmentTransform(start, end);
  if (!transform) return null;

  return (
    <mesh
      position={transform.position}
      quaternion={transform.quaternion}
      onPointerDown={rightClick.onPointerDown}
      onPointerUp={rightClick.onPointerUp}
      onPointerOver={() => {
        hoveredRef.current = true;
        onHoverChange(true);
      }}
      onPointerOut={() => {
        hoveredRef.current = false;
        onHoverChange(false);
      }}
    >
      <cylinderGeometry args={[hitRadius, hitRadius, transform.length, 6]} />
      <meshBasicMaterial transparent opacity={0} depthWrite={false} />
    </mesh>
  );
}

type DuctRegisterProps = {
  position: [number, number, number];
  directionXZ: [number, number];
  kind: DuctRun3DGeometry['kind'];
  orientation: 'down' | 'up';
  shellMaterial: MeshStandardMaterial;
  plateMaterial: MeshStandardMaterial;
};

function DuctRegister({
  position,
  directionXZ,
  kind,
  orientation,
  shellMaterial,
  plateMaterial
}: DuctRegisterProps) {
  const isSupply = kind === 'supply';
  const shellRadius = isSupply ? SUPPLY_DUCT_RADIUS : RETURN_DUCT_RADIUS;
  const boot = isSupply ? SUPPLY_REGISTER_BOOT : RETURN_REGISTER_BOOT;
  const plate = isSupply ? SUPPLY_REGISTER_PLATE : RETURN_REGISTER_PLATE;

  const [dirX, dirZ] = directionXZ;
  const rotationY = Math.atan2(-dirZ, dirX);

  const facingUp = orientation === 'up';
  const bootCenterY = facingUp
    ? boot[1] / 2 - shellRadius
    : shellRadius - boot[1] / 2;
  const plateCenterY = facingUp
    ? boot[1] + plate[1] / 2 - shellRadius
    : shellRadius - boot[1] - plate[1] / 2;

  return (
    <group position={position} rotation={[0, rotationY, 0]}>
      <mesh
        position={[0, bootCenterY, 0]}
        material={shellMaterial}
        renderOrder={1}
      >
        <boxGeometry args={boot} />
      </mesh>
      <mesh
        position={[0, plateCenterY, 0]}
        material={plateMaterial}
        renderOrder={1}
      >
        <boxGeometry args={plate} />
      </mesh>
    </group>
  );
}

type DuctworkRun3DProps = {
  run: DuctRun3DGeometry;
  color: string;
  opacity: number;
  revealed: boolean;
  editable: boolean;
  registerOrientation: 'down' | 'up';
  onHoverChange: (hovered: boolean) => void;
  onSegmentContextMenu: (
    event: ThreeEvent<PointerEvent>,
    segmentIndex: number
  ) => void;
};

function DuctworkRun3D({
  run,
  color,
  opacity,
  revealed,
  editable,
  registerOrientation,
  onHoverChange,
  onSegmentContextMenu
}: DuctworkRun3DProps) {
  const shellRadius =
    run.kind === 'supply' ? SUPPLY_DUCT_RADIUS : RETURN_DUCT_RADIUS;
  const guideOpacity = (run.kind === 'supply' ? 0.7 : 0.85) * opacity;

  const shellMaterial = useMemo(
    () =>
      new MeshStandardMaterial({
        color: DUCT_SHELL_COLOR,
        metalness: DUCT_SHELL_METALNESS,
        roughness: DUCT_SHELL_ROUGHNESS,
        transparent: true,
        opacity: revealed
          ? DUCT_SHELL_REVEALED_OPACITY
          : DUCT_SHELL_OPACITY * opacity,
        depthWrite: !revealed
      }),
    [revealed, opacity]
  );

  const plateMaterial = useMemo(
    () =>
      new MeshStandardMaterial({
        color: REGISTER_PLATE_COLOR,
        metalness: 0.3,
        roughness: 0.55,
        transparent: true,
        opacity: revealed
          ? DUCT_SHELL_REVEALED_OPACITY
          : DUCT_SHELL_OPACITY * opacity,
        depthWrite: !revealed
      }),
    [revealed, opacity]
  );

  const joints = useMemo(() => {
    const list: Array<{ key: string; position: [number, number, number] }> = [];
    const firstSegment = run.segments[0];
    if (firstSegment) {
      list.push({ key: `${run.key}::origin`, position: firstSegment.start });
    }
    run.points.forEach((point) => {
      if (point.pointUniqueId) {
        list.push({
          key: `${point.key}::joint`,
          position: [point.x, point.y, point.z]
        });
      }
    });
    return list;
  }, [run]);

  const register = useMemo(() => {
    const endpoint = run.points.find((point) => point.pointUniqueId === null);
    const lastSegment = run.segments[run.segments.length - 1];
    if (!endpoint || !lastSegment) return null;

    const dx = lastSegment.end[0] - lastSegment.start[0];
    const dz = lastSegment.end[2] - lastSegment.start[2];
    const length = Math.hypot(dx, dz);
    const directionXZ: [number, number] =
      length > 0.001 ? [dx / length, dz / length] : [1, 0];

    return {
      position: [endpoint.x, endpoint.y, endpoint.z] as [
        number,
        number,
        number
      ],
      directionXZ
    };
  }, [run]);

  return (
    <group>
      {run.segments.map((segment) => {
        const transform = computeSegmentTransform(segment.start, segment.end);
        return (
          <group key={segment.key}>
            <DuctGuideSegment
              start={segment.start}
              end={segment.end}
              color={color}
              opacity={guideOpacity}
            />
            {transform && (
              <mesh
                position={transform.position}
                quaternion={transform.quaternion}
                material={shellMaterial}
                renderOrder={1}
              >
                <cylinderGeometry
                  args={[shellRadius, shellRadius, transform.length, 16]}
                />
              </mesh>
            )}
            {editable && (
              <DuctSegmentHitArea
                start={segment.start}
                end={segment.end}
                hitRadius={shellRadius + DUCT_HIT_MARGIN}
                onContextMenu={(event) =>
                  onSegmentContextMenu(event, segment.index)
                }
                onHoverChange={onHoverChange}
              />
            )}
          </group>
        );
      })}
      {joints.map((joint) => (
        <mesh
          key={joint.key}
          position={joint.position}
          material={shellMaterial}
          renderOrder={1}
        >
          <sphereGeometry args={[shellRadius, 16, 16]} />
        </mesh>
      ))}
      {register && (
        <DuctRegister
          position={register.position}
          directionXZ={register.directionXZ}
          kind={run.kind}
          orientation={registerOrientation}
          shellMaterial={shellMaterial}
          plateMaterial={plateMaterial}
        />
      )}
    </group>
  );
}

type DraggableDuctPointProps = {
  point: DuctPoint3DGeometry;
  color: string;
  radius: number;
  opacity: number;
  selected: boolean;
  yOffset: number;
  runWorldHeight: number;
  clampPx: (proposedPx: PointPx) => PointPx | null;
  onClick: (event: ThreeEvent<MouseEvent>) => void;
  onContextMenu: (event: ThreeEvent<PointerEvent>) => void;
  onHoverChange: (hovered: boolean) => void;
  onDragStart: () => void;
  onDragChange: (px: PointPx) => void;
  onDragEnd: (px: PointPx | null) => void;
};

function DraggableDuctPoint({
  point,
  color,
  radius,
  opacity,
  selected,
  yOffset,
  runWorldHeight,
  clampPx,
  onClick,
  onContextMenu,
  onHoverChange,
  onDragStart,
  onDragChange,
  onDragEnd
}: DraggableDuctPointProps) {
  const pointRef = useRef(point);
  const dragStartPxRef = useRef(point.px);
  const clampPxRef = useRef(clampPx);
  const gizmoInteractionRef = useRef<TransformControlsInteractionState>({
    hovered: false,
    dragging: false
  });

  pointRef.current = point;
  clampPxRef.current = clampPx;

  const dragHandlers = useDirectPointDrag({
    pointPosition: [point.x, point.y, point.z],
    isBlocked: () =>
      gizmoInteractionRef.current.hovered ||
      gizmoInteractionRef.current.dragging,
    onDragStart: () => {
      dragStartPxRef.current = pointRef.current.px;
      onDragStart();
    },
    onDragChange: (localPosition) => {
      onDragChange(
        resolveDuctPointDrag(
          localPosition,
          yOffset,
          clampPxRef.current,
          runWorldHeight
        )
      );
    },
    onDragEnd: (localPosition) => {
      if (!localPosition) {
        onDragEnd(null);
        return;
      }
      const px = resolveDuctPointDrag(
        localPosition,
        yOffset,
        clampPxRef.current,
        runWorldHeight
      );
      const unchanged = isDuctPointDragUnchanged(dragStartPxRef.current, px);
      onDragEnd(unchanged ? null : px);
    }
  });

  if (selected) {
    return (
      <DuctPointGizmo
        pointGeometry={point}
        yOffset={yOffset}
        runWorldHeight={runWorldHeight}
        color={color}
        radius={radius}
        opacity={opacity}
        clampPx={clampPx}
        onDragStart={onDragStart}
        onDragChange={onDragChange}
        onDragEnd={onDragEnd}
        onContextMenu={onContextMenu}
        dragHandlers={dragHandlers}
        onInteractionChange={(state) => {
          gizmoInteractionRef.current = state;
        }}
      />
    );
  }

  return (
    <DuctPoint3D
      position={[point.x, point.y, point.z]}
      color={color}
      radius={radius}
      opacity={opacity}
      onClick={onClick}
      onContextMenu={onContextMenu}
      onHoverChange={onHoverChange}
      dragHandlers={dragHandlers}
    />
  );
}

type EditableDuctRuns3DProps = {
  layout: ZoneConcealedDuctLayout;
  structure: LevelType[];
  configurationId: string;
  yOffset: number;
  opacity: number;
  offsetX: number;
  offsetZ: number;
  selectedPointKey: string | null;
  onSelectPoint: (pointKey: string | null) => void;
  readOnly?: boolean;
};

export default function EditableDuctRuns3D({
  layout,
  structure,
  configurationId,
  yOffset,
  opacity,
  offsetX,
  offsetZ,
  selectedPointKey,
  onSelectPoint,
  readOnly = false
}: EditableDuctRuns3DProps) {
  const { openContextMenu } = useContextMenu();
  const actions = useDuctRunActions(configurationId);
  const [dragOverride, setDragOverride] = useState<DuctDragOverride | null>(
    null
  );
  const runHoverCountsRef = useRef<Map<string, number>>(new Map());
  const [hoveredRunKeys, setHoveredRunKeys] = useState<Set<string>>(new Set());

  const editable = !readOnly && opacity >= 1;
  const dragId = `duct-3d-${layout.zoneUniqueId}`;
  const runWorldHeight = getConcealedDuctRunWorldHeight(layout.placement);
  const registerOrientation = layout.placement === 'basement' ? 'up' : 'down';

  const gridSpaces = useMemo(
    () => getGridSpacesForLevel(structure, layout.originLevelIndex),
    [structure, layout.originLevelIndex]
  );

  const displayLayout = useMemo(
    () => applyDuctDragOverride(layout, dragOverride),
    [layout, dragOverride]
  );

  const geometry = useMemo(
    () => computeZoneDuct3DGeometry(displayLayout, yOffset),
    [displayLayout, yOffset]
  );

  const handleRunHoverChange = useCallback(
    (runKey: string, hovered: boolean) => {
      const counts = runHoverCountsRef.current;
      const nextCount = Math.max(
        (counts.get(runKey) ?? 0) + (hovered ? 1 : -1),
        0
      );
      if (nextCount === 0) {
        counts.delete(runKey);
      } else {
        counts.set(runKey, nextCount);
      }
      setHoveredRunKeys((prev) => {
        const shouldContain = nextCount > 0;
        if (prev.has(runKey) === shouldContain) return prev;
        const next = new Set(prev);
        if (shouldContain) {
          next.add(runKey);
        } else {
          next.delete(runKey);
        }
        return next;
      });
    },
    []
  );

  const handlePointClick = useCallback(
    (event: ThreeEvent<MouseEvent>, pointKey: string) => {
      if (event.delta > 5) return;
      event.stopPropagation();
      onSelectPoint(pointKey);
    },
    [onSelectPoint]
  );

  const buildRemoveRunMenuItem = useCallback(
    (run: DuctRun3DGeometry): ContextMenuItemType[] => [
      {
        label:
          run.kind === 'supply' ? 'Remove Supply Run' : 'Remove Return Run',
        disabled: !run.removable,
        onClick: () => {
          if (selectedPointKey && selectedPointKey.startsWith(`${run.key}::`)) {
            onSelectPoint(null);
          }
          actions.removeRun(run.runRef);
        }
      }
    ],
    [selectedPointKey, onSelectPoint, actions]
  );

  const handlePointContextMenu = useCallback(
    (
      event: ThreeEvent<PointerEvent>,
      run: DuctRun3DGeometry,
      point: DuctPoint3DGeometry
    ) => {
      const menuItems: ContextMenuItemType[] = [];

      if (point.pointUniqueId) {
        const { pointUniqueId } = point;
        menuItems.push({
          label: 'Delete Point',
          onClick: () => {
            if (selectedPointKey === point.key) {
              onSelectPoint(null);
            }
            actions.deleteWaypoint(
              run.runRef,
              pointUniqueId,
              run.autoWaypoints ? run.waypointsPx : undefined,
              run.pinReturnEndpointPx
            );
          }
        });
      }

      menuItems.push(...buildRemoveRunMenuItem(run));

      if (menuItems.length === 0) return;

      event.stopPropagation();
      event.nativeEvent.preventDefault();
      openContextMenu(
        event.nativeEvent.clientX,
        event.nativeEvent.clientY,
        menuItems,
        `duct-3d-point-${point.key}`
      );
    },
    [
      selectedPointKey,
      onSelectPoint,
      actions,
      buildRemoveRunMenuItem,
      openContextMenu
    ]
  );

  const handleSegmentContextMenu = useCallback(
    (
      event: ThreeEvent<PointerEvent>,
      run: DuctRun3DGeometry,
      segmentIndex: number
    ) => {
      const clickedDuctPoint = event.intersections.some(
        (intersection) => intersection.object.userData?.isDuctRunPoint === true
      );
      if (clickedDuctPoint) return;

      event.stopPropagation();
      event.nativeEvent.preventDefault();

      const clickPx = ductWorldToPx(
        event.point.x - offsetX,
        event.point.z - offsetZ
      );

      const menuItems: ContextMenuItemType[] = [
        {
          label: 'Add Point',
          onClick: () => {
            actions.addWaypoint(
              run.runRef,
              segmentIndex,
              clickPx,
              run.pinReturnEndpointPx,
              run.autoWaypoints ? run.waypointsPx : undefined
            );
          }
        },
        ...buildRemoveRunMenuItem(run)
      ];

      openContextMenu(
        event.nativeEvent.clientX,
        event.nativeEvent.clientY,
        menuItems,
        `duct-3d-segment-${run.key}-${segmentIndex}`
      );
    },
    [offsetX, offsetZ, actions, buildRemoveRunMenuItem, openContextMenu]
  );

  const makeClampPx = useCallback(
    (run: DuctRun3DGeometry, point: DuctPoint3DGeometry) =>
      (proposedPx: PointPx): PointPx | null => {
        if (point.pointUniqueId) return proposedPx;
        if (run.kind === 'supply') {
          return clampSupplyEndpointPx(
            proposedPx,
            run.structureSpaceId,
            gridSpaces
          );
        }
        return clampReturnEndpointPx(proposedPx, layout, gridSpaces);
      },
    [gridSpaces, layout]
  );

  const handleDragStart = useCallback(() => {
    beginLinesetDrag(dragId);
  }, [dragId]);

  const makeDragChangeHandler = useCallback(
    (run: DuctRun3DGeometry, point: DuctPoint3DGeometry) => (px: PointPx) => {
      setDragOverride({
        runRef: run.runRef,
        pointUniqueId: point.pointUniqueId,
        px
      });
    },
    []
  );

  const makeDragEndHandler = useCallback(
    (run: DuctRun3DGeometry, point: DuctPoint3DGeometry) =>
      (px: PointPx | null) => {
        if (px) {
          if (point.pointUniqueId) {
            actions.moveWaypoint(
              run.runRef,
              point.pointUniqueId,
              px,
              run.autoWaypoints ? run.waypointsPx : undefined,
              run.pinReturnEndpointPx
            );
          } else {
            actions.moveEndpoint(run.runRef, px);
          }
        }
        setDragOverride(null);
        setTimeout(() => {
          endLinesetDrag(dragId);
        }, 0);
      },
    [actions, dragId]
  );

  if (!geometry) return null;

  return (
    <group>
      {geometry.runs.map((run) => {
        const color = run.kind === 'supply' ? SUPPLY_COLOR : RETURN_COLOR;
        const revealed =
          editable &&
          (hoveredRunKeys.has(run.key) ||
            (!!selectedPointKey &&
              selectedPointKey.startsWith(`${run.key}::`)));

        return (
          <group key={run.key}>
            <DuctworkRun3D
              run={run}
              color={color}
              opacity={opacity}
              revealed={revealed}
              editable={editable}
              registerOrientation={registerOrientation}
              onHoverChange={(hovered) =>
                handleRunHoverChange(run.key, hovered)
              }
              onSegmentContextMenu={(event, segmentIndex) =>
                handleSegmentContextMenu(event, run, segmentIndex)
              }
            />
            {editable &&
              run.points.map((point) => (
                <DraggableDuctPoint
                  key={point.key}
                  point={point}
                  color={color}
                  radius={
                    point.pointUniqueId ? WAYPOINT_RADIUS : ENDPOINT_RADIUS
                  }
                  opacity={opacity}
                  selected={selectedPointKey === point.key}
                  yOffset={yOffset}
                  runWorldHeight={runWorldHeight}
                  clampPx={makeClampPx(run, point)}
                  onClick={(event) => handlePointClick(event, point.key)}
                  onContextMenu={(event) =>
                    handlePointContextMenu(event, run, point)
                  }
                  onHoverChange={(hovered) =>
                    handleRunHoverChange(run.key, hovered)
                  }
                  onDragStart={handleDragStart}
                  onDragChange={makeDragChangeHandler(run, point)}
                  onDragEnd={makeDragEndHandler(run, point)}
                />
              ))}
          </group>
        );
      })}
    </group>
  );
}
